import usei18n from "@app-i18n/index";
import { getMutationContext, resolveMutationContext } from "@app-store/persistent.plugin";
import useReservationStore from "@app-store/reservation";
import useSystemStore from "@app-store/system";
import useUserStore, { UserModuleInit } from "@app-store/user";
import { h } from "vue";

import { format } from "./dates";
import { modalOk, modalOptions, modalWarningConfirm } from "./modals";
import { trackUserEvent } from "./observability";
import { AppInsightsEvent } from "./observability-events";
import { waitForSystem } from "./system";

let _isNotifyingSelectedOfferNotAvailable: Promise<boolean | null | undefined> | null = null;
export function notifySelectedOfferNotAvailable() {
	if (_isNotifyingSelectedOfferNotAvailable) return _isNotifyingSelectedOfferNotAvailable;

	const { translateKey } = usei18n();
	const content = [
		h("div", translateKey("weboffers_selection_not_available")),
		h("div", translateKey("weboffers_close_to_check_available"))
	];
	_isNotifyingSelectedOfferNotAvailable = modalOk(content, {
		title: translateKey("label_information"),
		hideHeaderClose: false,
		"data-test": "Modal-SelectedOfferNotAvailable"
	});
	_isNotifyingSelectedOfferNotAvailable?.finally(() => (_isNotifyingSelectedOfferNotAvailable = null));

	return _isNotifyingSelectedOfferNotAvailable;
}
export function notifyReservationExpired() {
	const { translateKey } = usei18n();
	return modalOk([
		h("div", translateKey("weboffers_reservation_expired"))
	], {
		title: translateKey("label_information"),
		"data-test": "Modal-Reservation-Expired"
	});
}
export function notifyReservationConfirmInProgress() {
	const { translateKey } = usei18n();
	const content = [
		h("div", translateKey("common_error_system_is_processing_your_request"))
	];
	return modalOk(content, {
		title: translateKey("label_information"),
		hideHeaderClose: false,
		"data-test": "Modal-ReservationConfirmInProgress"
	});
}
export function notifyReservationAlreadyConfirmed() {
	const { translateKey } = usei18n();
	const content = [
		h("div", translateKey("reservation_already_confirmed"))
	];
	return modalOk(content, {
		title: translateKey("label_information"),
		hideHeaderClose: false,
		"data-test": "Modal-ReservationAlreadyConfirmed"
	});
}
let _isAskingForResume: Promise<boolean> | null = null;
export function notifyReservationResumeAvailable() {
	if (_isAskingForResume) return _isAskingForResume;

	trackUserEvent(AppInsightsEvent.ReservationResumeAsk);
	const { translateKey } = usei18n();
	const reservationStore = useReservationStore();
	const summary = () => {
		const bowlersLabelKey = (reservationStore.bowlersTotal === 1 ? "label_bowler" : "label_bowlers");
		const date = reservationStore.pickedDatetime ? format(reservationStore.pickedDatetime, "P") : "";
		const time = reservationStore.pickedDatetime ? format(reservationStore.pickedDatetime, "p") : "";
		const reservationHasFormData = reservationStore.hasReservationFormData();
		return reservationHasFormData && reservationStore.offer
			? `${reservationStore.bowlersTotal} ${translateKey(bowlersLabelKey)} on ${date} at ${time}`
			: "";
	};
	const content = [
		h("div", translateKey("weboffers_reservation_left")),
		h("br", ""),
		h("b", useSystemStore().info?.Name),
		h("br", ""),
		h("b", summary()),
		h("br", ""),
		h("br", ""),
		h("div", translateKey("weboffers_reservation_new_question"))
	];
	_isAskingForResume = modalOptions(content, [
		{ label: translateKey("label_start_new"), value: false },
		{ label: translateKey("label_continue"), value: true, variant: "primary" }
	], {
		title: translateKey("weboffer_reservation_left_title"),
		modalClass: "modal-reservation-resume-available",
		"data-test": "Modal-ReservationResumeAvailable"
	});
	_isAskingForResume?.then(isResumeAccepted => trackUserEvent(isResumeAccepted ? AppInsightsEvent.ReservationResumeAccepted : AppInsightsEvent.ReservationResumeCanceled))
		.finally(() => (_isAskingForResume = null));
	return _isAskingForResume;
}
export function notifyReservationLeftNotAvailable() {
	const { translateKey } = usei18n();
	const content = [
		h("div", translateKey("weboffers_reservation_left")),
		h("div", translateKey("weboffers_reservation_new"))
	];
	return modalOk(content, {
		title: translateKey("label_information"),
		"data-test": "Modal-ReservationLeftNotAvailable"
	});
}
export function notifyBackToWebOffersPage() {
	const { translateKey } = usei18n();
	return modalWarningConfirm(
		[
			h("div", translateKey("common_change_offer_items_currently_in_cart_will_removed"))
		],
		{
			modalClass: "modal-utility modal-changing-offer",
			hideHeaderClose: true,
			title: translateKey("common_changing_offer"),
			okTitle: translateKey("label_empty_cart"),
			cancelTitle: translateKey("label_keep_my_offer"),
			"data-test": "Modal-BackToWebOffersPage"
		}
	);
}

export async function persistenceKey(email?: string, resKey?: string) {
	const ReservationStore = useReservationStore();
	const SystemStore = useSystemStore();
	const UserStore = useUserStore();
	if (!ReservationStore.reservationKey && !resKey) return;
	await Promise.all([
		UserModuleInit,
		waitForSystem()
	]);
	if (!SystemStore.info) return;

	const user = email || (UserStore.isLoggedIn() ? UserStore.userData().Email : "guest");
	const systemId = Number(useSystemStore().info?.Id);
	const reservationKey = resKey ?? ReservationStore.reservationKey;
	return `${user}-${systemId}-${reservationKey}`;
}
export async function clearFluxPersistence() {
	const key = await persistenceKey();
	if (!key) return;
	const cartDbContext = getMutationContext("cart");
	const reservationDbContext = getMutationContext("reservation");
	if (!cartDbContext || !reservationDbContext) return;
	const cartCtx = cartDbContext.access();
	const resCtx = reservationDbContext.access();
	await resolveMutationContext(cartCtx.delete(key));
	await resolveMutationContext(resCtx.delete(key));
}
